// useCenterThresholdTheme.js
import { useEffect } from "react";

/**
 * Switch theme based on which section crosses a vertical line in the viewport.
 * @param {object} opts
 * @param {string} opts.selector  CSS selector for themed sections
 * @param {number} opts.line      0..1 vertical position (0 = top, 0.5 = center, 1 = bottom)
 * @param {number} opts.hysteresisPx Optional stickiness in px to avoid rapid flips near edges
 */
export function useCenterThresholdTheme({
  selector = "[data-theme-section]",
  line = 0.5,
  hysteresisPx = 8,
} = {}) {
  useEffect(() => {
    const els = Array.from(document.querySelectorAll(selector));
    if (!els.length) return;

    const apply = (el) => {
        const bg = el.dataset.bg || "#fff";
        const fg = el.dataset.fg || "#111";
        const bgTop = el.dataset.bgTop || bg;
        const bgBottom = el.dataset.bgBottom || bg;
        const glow = el.dataset.glow || "rgba(0,0,0,0)";
    
        document.documentElement.style.setProperty("--bg", bg);
        document.documentElement.style.setProperty("--fg", fg);
        document.documentElement.style.setProperty("--bg-top", bgTop);
        document.documentElement.style.setProperty("--bg-bottom", bgBottom);
        document.documentElement.style.setProperty("--glow", glow);
    };


    let activeEl = null;

    const pickActive = () => {
      const vh = window.innerHeight || 0;
      const y = vh * line;

      // Find the section that contains the decision line (y)
      let current = els.find((el) => {
        const r = el.getBoundingClientRect();
        return r.top - hysteresisPx <= y && y <= r.bottom + hysteresisPx;
      });

      // Fallback: pick the one whose midpoint is closest to the line
      if (!current) {
        let best = null;
        let bestScore = Infinity;
        for (const el of els) {
          const r = el.getBoundingClientRect();
          // skip if not on screen at all
          if (r.bottom <= 0 || r.top >= vh) continue;
          const mid = (r.top + r.bottom) / 2;
          const score = Math.abs(mid - y);
          if (score < bestScore) { best = el; bestScore = score; }
        }
        current = best || activeEl;
      }

      if (current && current !== activeEl) {
        activeEl = current;
        apply(activeEl);
      }
    };

    // rAF-throttled scroll/resize handler
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        pickActive();
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    // initialize immediately after first paint
    requestAnimationFrame(pickActive);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [selector, line, hysteresisPx]);
}
